import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Sky } from 'three/addons/objects/Sky.js';
import GUI from 'lil-gui';

export function initHauntedHouse(canvas) {
    /**
     * Base
     */
    // Debug
    const gui = new GUI({
        width: 400,
        title: 'Debug Menu',
        closeFolders: false,
    });

    // gui.close()
    gui.hide();

    function handleKeyDown(event) {
        if (event.key === 'h') gui.show(gui._hidden);
    }
    window.addEventListener('keydown', handleKeyDown);

    // Scene
    const scene = new THREE.Scene();

    /**
     * Textures
     */
    const textureLoader = new THREE.TextureLoader();

    // Floor
    const floorAlphaTexture = textureLoader.load('/floor/alpha.webp');
    const floorColorTexture = textureLoader.load('/floor/coast_sand_rocks_02_1k/coast_sand_rocks_02_diff_1k.webp');
    const floorARMTexture = textureLoader.load('/floor/coast_sand_rocks_02_1k/coast_sand_rocks_02_arm_1k.webp');
    const floorNormalTexture = textureLoader.load('/floor/coast_sand_rocks_02_1k/coast_sand_rocks_02_nor_gl_1k.webp');
    const floorDisplacementTexture = textureLoader.load('/floor/coast_sand_rocks_02_1k/coast_sand_rocks_02_disp_1k.webp');

    floorAlphaTexture.colorSpace = THREE.SRGBColorSpace; // Important for color textures
    floorColorTexture.repeat.set(8, 8);
    floorARMTexture.repeat.set(8, 8);
    floorNormalTexture.repeat.set(8, 8);
    floorDisplacementTexture.repeat.set(8, 8);

    floorColorTexture.wrapT = THREE.RepeatWrapping;
    floorARMTexture.wrapT = THREE.RepeatWrapping;
    floorNormalTexture.wrapT = THREE.RepeatWrapping;
    floorDisplacementTexture.wrapT = THREE.RepeatWrapping;

    floorColorTexture.wrapS = THREE.RepeatWrapping;
    floorARMTexture.wrapS = THREE.RepeatWrapping;
    floorNormalTexture.wrapS = THREE.RepeatWrapping;
    floorDisplacementTexture.wrapS = THREE.RepeatWrapping;

    // Walls
    const wallsColorTexture = textureLoader.load('/wall/castle_brick_broken_06_1k/castle_brick_broken_06_diff_1k.webp');
    const wallsARMTexture = textureLoader.load('/wall/castle_brick_broken_06_1k/castle_brick_broken_06_arm_1k.webp');
    const wallsNormalTexture = textureLoader.load('/wall/castle_brick_broken_06_1k/castle_brick_broken_06_nor_gl_1k.webp');

    wallsColorTexture.colorSpace = THREE.SRGBColorSpace;

    wallsColorTexture.repeat.set(1.5, 1.5);
    wallsARMTexture.repeat.set(1.5, 1.5);
    wallsNormalTexture.repeat.set(1.5, 1.5);

    wallsColorTexture.wrapT = THREE.RepeatWrapping;
    wallsARMTexture.wrapT = THREE.RepeatWrapping;
    wallsNormalTexture.wrapT = THREE.RepeatWrapping;

    wallsColorTexture.wrapS = THREE.RepeatWrapping;
    wallsARMTexture.wrapS = THREE.RepeatWrapping;
    wallsNormalTexture.wrapS = THREE.RepeatWrapping;

    // Roof
    const roofColorTexture = textureLoader.load('/roof/roof_slates_02_1k/roof_slates_02_diff_1k.webp');
    const roofARMTexture = textureLoader.load('/roof/roof_slates_02_1k/roof_slates_02_arm_1k.webp');
    const roofNormalTexture = textureLoader.load('/roof/roof_slates_02_1k/roof_slates_02_nor_gl_1k.webp');

    roofColorTexture.colorSpace = THREE.SRGBColorSpace;

    roofColorTexture.repeat.set(3, 1);
    roofARMTexture.repeat.set(3, 1);
    roofNormalTexture.repeat.set(3, 1);

    roofColorTexture.wrapS = THREE.RepeatWrapping;
    roofARMTexture.wrapS = THREE.RepeatWrapping;
    roofNormalTexture.wrapS = THREE.RepeatWrapping;

    // Bush
    const bushColorTexture = textureLoader.load('/bush/leaves_forest_ground_1k/leaves_forest_ground_diff_1k.webp');
    const bushARMTexture = textureLoader.load('/bush/leaves_forest_ground_1k/leaves_forest_ground_arm_1k.webp');
    const bushNormalTexture = textureLoader.load('/bush/leaves_forest_ground_1k/leaves_forest_ground_nor_gl_1k.webp');

    bushColorTexture.colorSpace = THREE.SRGBColorSpace;

    bushColorTexture.repeat.set(2, 1); // x, y
    bushARMTexture.repeat.set(2, 1);
    bushNormalTexture.repeat.set(2, 1);

    bushColorTexture.wrapS = THREE.RepeatWrapping;
    bushARMTexture.wrapS = THREE.RepeatWrapping;
    bushNormalTexture.wrapS = THREE.RepeatWrapping;

    // Grave
    const graveColorTexture = textureLoader.load('/grave/plastered_stone_wall_1k/plastered_stone_wall_diff_1k.webp');
    const graveARMTexture = textureLoader.load('/grave/plastered_stone_wall_1k/plastered_stone_wall_arm_1k.webp');
    const graveNormalTexture = textureLoader.load('/grave/plastered_stone_wall_1k/plastered_stone_wall_nor_gl_1k.webp');

    graveColorTexture.colorSpace = THREE.SRGBColorSpace;

    graveColorTexture.repeat.set(0.3, 0.4);
    graveARMTexture.repeat.set(0.3, 0.4);
    graveNormalTexture.repeat.set(0.3, 0.4);

    // Door
    const doorColorTexture = textureLoader.load('/door/color.webp');
    const doorAlphaTexture = textureLoader.load('/door/alpha.webp');
    const doorAmbientOcclusionTexture = textureLoader.load('/door/ambientOcclusion.webp');
    const doorHeightTexture = textureLoader.load('/door/height.webp');
    const doorNormalTexture = textureLoader.load('/door/normal.webp');
    const doorMetalnessTexture = textureLoader.load('/door/metalness.webp');
    const doorRoughnessTexture = textureLoader.load('/door/roughness.webp');

    doorColorTexture.colorSpace = THREE.SRGBColorSpace;

    /**
     * House
     */
    // Floor
    const floor = new THREE.Mesh(
        new THREE.PlaneGeometry(20, 20, 100, 100), // width, height, widthSegments, heightSegments
        new THREE.MeshStandardMaterial({
            alphaMap: floorAlphaTexture,
            transparent: true,
            map: floorColorTexture,
            aoMap: floorARMTexture,
            roughnessMap: floorARMTexture,
            metalnessMap: floorARMTexture,
            normalMap: floorNormalTexture,
            displacementMap: floorDisplacementTexture,
            displacementScale: 0.3,
            displacementBias: -0.15,
        }),
    );

    floor.rotation.x = -Math.PI * 0.5;
    scene.add(floor);

    gui.add(floor.material, 'displacementScale').min(0).max(1).step(0.001).name('Floor Displacement Scale');
    gui.add(floor.material, 'displacementBias').min(-1).max(1).step(0.001).name('Floor Displacement Scale');
    gui.hideen = true;

    // House container
    const house = new THREE.Group();
    scene.add(house);

    // Walls
    const walls = new THREE.Mesh(
        new THREE.BoxGeometry(4, 2.5, 4), // width, height, depth
        new THREE.MeshStandardMaterial({
            map: wallsColorTexture,
            aoMap: wallsARMTexture,
            roughnessMap: wallsARMTexture,
            metalnessMap: wallsARMTexture,
            normalMap: wallsNormalTexture,
        }),
    );
    walls.position.y = 1.25;
    house.add(walls);

    // Roof
    const roof = new THREE.Mesh(
        new THREE.ConeGeometry(3.5, 1.5, 4), // radius, height, radialSegments
        new THREE.MeshStandardMaterial({
            map: roofColorTexture,
            aoMap: roofARMTexture,
            roughnessMap: roofARMTexture,
            metalnessMap: roofARMTexture,
            normalMap: roofNormalTexture,
        }),
    );
    roof.position.y = 2.5 + 0.75;
    roof.rotation.y = Math.PI * 0.25;
    house.add(roof);

    // Door
    const door = new THREE.Mesh(
        new THREE.PlaneGeometry(2.2, 2.2, 100, 100),
        new THREE.MeshStandardMaterial({
            map: doorColorTexture,
            transparent: true,
            alphaMap: doorAlphaTexture,
            aoMap: doorAmbientOcclusionTexture,
            displacementMap: doorHeightTexture,
            displacementScale: 0.15,
            displacementBias: -0.04,
            normalMap: doorNormalTexture,
            metalnessMap: doorMetalnessTexture,
            roughnessMap: doorRoughnessTexture,
        }),
    );
    door.position.y = 1;
    door.position.z = 2 + 0.01;
    house.add(door);

    // Bushes
    const bushGeometry = new THREE.SphereGeometry(1, 16, 16);
    const bushMaterial = new THREE.MeshStandardMaterial({
        color: '#89c854',
        map: bushColorTexture,
        aoMap: bushARMTexture,
        roughnessMap: bushARMTexture,
        metalnessMap: bushARMTexture,
        normalMap: bushNormalTexture,
    });

    const bush1 = new THREE.Mesh(bushGeometry, bushMaterial); // mesh = geometry + material
    bush1.scale.set(0.5, 0.5, 0.5);
    bush1.position.set(0.8, 0.2, 2.2);
    bush1.rotation.x = -0.75;

    const bush2 = new THREE.Mesh(bushGeometry, bushMaterial);
    bush2.scale.set(0.25, 0.25, 0.25);
    bush2.position.set(1.4, 0.1, 2.1);
    bush2.rotation.x = -0.75;

    const bush3 = new THREE.Mesh(bushGeometry, bushMaterial);
    bush3.scale.set(0.4, 0.4, 0.4);
    bush3.position.set(-0.8, 0.1, 2.2);
    bush3.rotation.x = -0.75;

    const bush4 = new THREE.Mesh(bushGeometry, bushMaterial);
    bush4.scale.set(0.15, 0.15, 0.15);
    bush4.position.set(-1, 0.05, 2.6);
    bush4.rotation.x = -0.75;

    house.add(bush1, bush2, bush3, bush4);

    // Graves
    const graves = new THREE.Group();
    scene.add(graves);

    const graveGeometry = new THREE.BoxGeometry(0.6, 0.8, 0.2);
    const graveMaterial = new THREE.MeshStandardMaterial({
        map: graveColorTexture,
        aoMap: graveARMTexture,
        roughnessMap: graveARMTexture,
        metalnessMap: graveARMTexture,
        normalMap: graveNormalTexture,
    });

    for (let i = 0; i < 30; i++) {
        // Position
        const angle = Math.random() * Math.PI * 2;
        const radius = 3 + Math.random() * 4;
        const x = Math.sin(angle) * radius;
        const z = Math.cos(angle) * radius;

        // Mesh
        const grave = new THREE.Mesh(graveGeometry, graveMaterial);
        grave.position.x = x;
        grave.position.y = Math.random() * 0.4;
        grave.position.z = z;
        grave.rotation.x = (Math.random() - 0.5) * 0.4;
        grave.rotation.y = (Math.random() - 0.5) * 0.4;
        grave.rotation.z = (Math.random() - 0.5) * 0.4;

        // console.log((Math.random() - 0.5) * 0.4);

        // Add to graves group
        graves.add(grave);
    }

    /**
     * Lights
     */
    // Ambient light
    const ambientLight = new THREE.AmbientLight('rgb(49, 53, 92)', 0.275 * 2);
    scene.add(ambientLight);

    // Directional light
    const directionalLight = new THREE.DirectionalLight('#86cdff', 1 * 2);
    directionalLight.position.set(3, 2, -8);
    scene.add(directionalLight);

    // Door light
    const doorLight = new THREE.PointLight('#b97050', 2);
    doorLight.position.set(0, 2.2, 2.5);
    house.add(doorLight);

    /**
     * Ghosts
     */
    const ghost1 = new THREE.PointLight('#504c6a', 6);
    const ghost2 = new THREE.PointLight('#5a4461', 6);
    const ghost3 = new THREE.PointLight('#649595', 6);

    scene.add(ghost1, ghost2, ghost3);

    /**
     * Sizes
     */
    const sizes = {
        width: 0,
        height: 0,
    };

    /**
     * Camera
     */
    // Base camera
    const camera = new THREE.PerspectiveCamera(100, sizes.width / sizes.height, 0.1, 100); // fov, aspect, near, far
    camera.position.x = 4;
    camera.position.y = 2;
    camera.position.z = 5;
    scene.add(camera);

    // Controls
    const controls = new OrbitControls(camera, canvas);
    controls.enableDamping = true;

    // 🛑 1. Limiter le zoom (approche / recul)
    controls.minDistance = 3;  // Empêche de rentrer dans les murs de la maison
    controls.maxDistance = 15; // Empêche de se perdre dans le vide noir

    // 🛑 2. Empêcher de passer sous le sol
    // Math.PI / 2 correspond à 90 degrés (regarder pile à l'horizon).
    // On met un tout petit peu moins (0.49) pour éviter que la caméra ne rentre dans les cailloux du sol.
    controls.maxPolarAngle = Math.PI * 0.49;

    /**
     * Renderer
     */
    const renderer = new THREE.WebGLRenderer({ canvas: canvas });
    renderer.setSize(sizes.width, sizes.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    /**
     * Resize handler
     */

    const updateSizes = () => {
        // On prend la largeur du parent ou de la fenêtre
        const containerWidth = canvas.parentElement.clientWidth;
        const width = Math.min(containerWidth, 700);

        sizes.width = width;
        sizes.height = width; // Pour garder le format carré 1:1

        // Mise à jour de la caméra
        camera.aspect = sizes.width / sizes.height;
        camera.updateProjectionMatrix();

        // Mise à jour du rendu
        renderer.setSize(sizes.width, sizes.height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    };
    updateSizes();

    window.addEventListener('resize', updateSizes);

    /**
     * Shadows
     */

    // Renderer
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1;

    // Cast and receive shadows
    directionalLight.castShadow = true;
    ghost1.castShadow = true;
    ghost2.castShadow = true;
    ghost3.castShadow = true;

    walls.castShadow = true;
    walls.receiveShadow = true;

    roof.castShadow = true;
    floor.receiveShadow = true;

    for (const grave of graves.children) {
        grave.castShadow = true;
        grave.receiveShadow = true;
    }

    // Mapping shadows
    directionalLight.shadow.mapSize.width = 256;
    directionalLight.shadow.mapSize.height = 256;
    directionalLight.shadow.camera.top = 8;
    directionalLight.shadow.camera.right = 8;
    directionalLight.shadow.camera.bottom = -8;
    directionalLight.shadow.camera.left = -8;
    directionalLight.shadow.camera.near = 1;
    directionalLight.shadow.camera.far = 20;

    ghost1.shadow.mapSize.width = 256;
    ghost1.shadow.mapSize.height = 256;
    ghost1.shadow.camera.far = 10;

    ghost2.shadow.mapSize.width = 256;
    ghost2.shadow.mapSize.height = 256;
    ghost2.shadow.camera.far = 10;

    ghost3.shadow.mapSize.width = 256;
    ghost3.shadow.mapSize.height = 256;
    ghost3.shadow.camera.far = 10;

    /**
     * Sky
     */
    const sky = new Sky();
    sky.scale.setScalar(100);
    scene.add(sky);

    sky.material.uniforms['turbidity'].value = 15; // quality of being cloudy, opaque
    sky.material.uniforms['rayleigh'].value = 3; // intensity of the sky's blue color
    sky.material.uniforms['mieCoefficient'].value = 0.1; // intensity of the sun's light scattering
    sky.material.uniforms['mieDirectionalG'].value = 0.95; // direction of the sun's light scattering (0 = isotropic, 1 = forward scattering, -1 = backward scattering)
    sky.material.uniforms['sunPosition'].value.set(0.3, -0.038, -0.95);

    /**
     * Fog
     */
    // scene.fog = new THREE.Fog('#04343f', 1, 15) // color, near, far
    scene.fog = new THREE.FogExp2('#04343f', 0.1); // color, density

    /**
     * Animate
     */
    const timer = new THREE.Timer();
    let animationId;

    const tick = () => {
        // Timer
        timer.update();
        const elapsedTime = timer.getElapsed();

        // Ghost
        const ghost1Angle = elapsedTime * 0.5;
        ghost1.position.x = Math.cos(ghost1Angle) * 4;
        ghost1.position.z = Math.sin(ghost1Angle) * 4;
        ghost1.position.y =
            Math.sin(ghost1Angle) *
            Math.sin(ghost1Angle * 2.34) *
            Math.sin(ghost1Angle * 2.34);

        const ghost2Angle = -elapsedTime * 0.38;
        ghost2.position.x = Math.cos(ghost2Angle) * 5;
        ghost2.position.z = Math.sin(ghost2Angle) * 5;
        ghost2.position.y =
            Math.sin(ghost2Angle) *
            Math.sin(ghost2Angle * 3.14) *
            Math.sin(ghost2Angle * 3.14);

        const ghost3Angle = elapsedTime * 0.23;
        ghost3.position.x = Math.cos(ghost3Angle) * 7;
        ghost3.position.z = Math.sin(ghost3Angle) * 7;
        ghost3.position.y =
            Math.sin(ghost3Angle) *
            Math.sin(ghost3Angle * 4.14) *
            Math.sin(ghost3Angle * 4.14);

        controls.update();
        renderer.render(scene, camera);

        animationId = window.requestAnimationFrame(tick);
    };

    tick();

    return () => {
        window.removeEventListener('resize', updateSizes);
        window.removeEventListener('keydown', handleKeyDown);
        cancelAnimationFrame(animationId);
        gui.destroy(); // On détruit le menu de debug pour ne pas l'avoir en double
        renderer.dispose();
    };
}